/**
 * The enrollment gate, as a screen.
 *
 * Two doors lead here and they are the same door. `POST /auth/invite/accept`
 * creates an account with `totp_required` set and issues NO session cookies,
 * and `POST /auth/login` answers `enrollment_required` for that account at
 * every later attempt until a TOTP code verifies. Both hand back the same
 * scoped `enrollment` token, which opens `/auth/2fa/enroll` and
 * `/auth/2fa/enroll/confirm` and nothing else in the service — so this screen
 * is not a policy the client is trusted to apply, it is the only thing the
 * credential in hand can actually do.
 *
 * Which is why there is no cancel and no "set this up later". A way out of the
 * requirement would not be a courtesy; it would be a loop, since the login it
 * led to would return the user here holding a fresh token.
 *
 * One exception, and it is not a way out: when the ten-minute token EXPIRES,
 * every call this screen can make 401s, so `onExpired` puts the user back on
 * the sign-in form to be handed a live one. Without it the screen tells them to
 * sign in again while rendering nothing they can press.
 *
 * The enrollment UI itself is `TwoFactorSetup`, unchanged from the profile
 * page apart from the token it now carries. One implementation of the QR, the
 * manual key, the code box and the once-only backup codes — a second copy is
 * how the two drift until one of them stops matching the server.
 */
import { SignedOutLanguageBar } from '../auth/LoginPage'
import { useI18n } from '../i18n'
import { TwoFactorSetup } from './TwoFactorSetup'

export interface EnrollmentStepProps {
  /** The scoped `enrollment` token. This screen never renders without one. */
  enrollmentToken: string
  /** The account this is for, when it is known — an invitee has just typed a
   *  password for an address the server chose, and seeing it confirms they
   *  redeemed the invite they meant to. */
  email?: string
  /**
   * The backup codes have been acknowledged and real session cookies exist
   * (`session_started`), so the caller can re-read `/auth/me` and land the user
   * in the app. Never called before the acknowledgement: the codes are shown
   * exactly once in the lifetime of the account.
   */
  onDone: () => void
  /**
   * The scoped token is spent (a 401 on the token path). The caller has to drop
   * it and put the user back in front of the sign-in form -- which is the
   * documented recovery, not a bypass: that login answers `enrollment_required`
   * and mints a fresh token for the same gate. Without it "sign in again" is an
   * instruction with nothing behind it, because this route holds the dead token
   * in state and re-renders this screen forever.
   */
  onExpired?: () => void
}

export function EnrollmentStep({
  enrollmentToken,
  email,
  onDone,
  onExpired,
}: EnrollmentStepProps) {
  const { t } = useI18n()

  return (
    // `min-h-full` with vertical padding rather than the centred `h-full` of
    // AuthCard: with the QR, the setup key and ten backup codes this card is
    // taller than a sign-in form and must be able to scroll rather than clip
    // the one screen of codes the user will ever see.
    <main className="flex min-h-full items-start justify-center bg-bg px-4 py-8">
      <div className="w-full max-w-md space-y-4">
        {/* Enrollment is still a signed-out screen — no `AppHeader`, so no
            switcher unless it is put here. It is also the longest-reading
            screen in the product (a QR, a setup key, ten backup codes), which
            makes it the worst one to be stuck reading in the wrong language. */}
        <SignedOutLanguageBar className="max-w-md" />
        <header className="space-y-1">
          <h1 className="text-lg font-semibold text-fg">{t('account.enrollment.title')}</h1>
          {/* One key per whole sentence rather than an address glued to a
              fragment: Chinese puts the account somewhere else in the clause
              and has no space to glue it with. */}
          <p className="text-xs text-muted">
            {email
              ? t('account.enrollment.introWithEmail', { email })
              : t('account.enrollment.intro')}
          </p>
        </header>

        <TwoFactorSetup
          enabled={false}
          enrollmentToken={enrollmentToken}
          required
          onAcknowledge={onDone}
          onExpired={onExpired}
        />
      </div>
    </main>
  )
}

export default EnrollmentStep
